import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import {
  UserService,
  SetTeamMemberParams,
} from 'netsocial-posting/src/app/services/user/user.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { User } from 'netsocial-lib/src/app/interfaces/user';
import { ErrorService } from 'netsocial-posting/src/app/services/error/error.service';
import { NotificationService } from 'netsocial-lib/src/app/services/notification/notification.service';
import { observable, computed } from 'mobx-angular';
import { Account } from 'netsocial-lib/src/app/interfaces/account';
import { AccountService } from 'netsocial-posting/src/app/services/account/account.service';
import { ChannelService } from 'netsocial-posting/src/app/services/channel/channel.service';
import { EditChannelComponent } from '../../channel/edit-channel/edit-channel.component';
import { Channel } from 'netsocial-lib/src/app/interfaces/channel';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent } from 'netsocial-posting/src/app/interfaces/base.component';
@Component({
  selector: 'app-onboarding-carousel',
  templateUrl: './onboarding-carousel.component.html',
  styleUrls: ['./onboarding-carousel.component.scss'],
})
export class OnboardingCarouselComponent extends BaseComponent implements OnInit {
  @ViewChild('editChannel', { static: false })
  editChannel: EditChannelComponent;

  @Input() stepObservable: BehaviorSubject<number>;
  @observable @Input() currentStep = 0;

  @Output() ionChangeStep: EventEmitter<number> | ((step: number) => void) = new EventEmitter();

  loadingUser: boolean;
  @observable loadingSave: boolean;

  @observable userToEdit?: User;
  @observable validPhone = true;
  @observable passwordStrength: number | null;

  search: string;
  numChannels = 0;

  onboardingChannel: Channel;

  @observable accounts?: Account[] = [];
  @observable accountIndex = 0;

  @computed
  get selectedAccount(): Account | undefined {
    return this.accounts && this.accounts.length ? this.accounts[this.accountIndex] : undefined;
  }

  @computed
  get nextDisabled(): boolean {
    const phoneInvalid = (this.userToEdit?.phone?.length ?? 0) > 0 && !this.validPhone;
    const noPassword = this.currentStep === 2 && !this.userToEdit?.password;
    const passwordStrength = this.passwordStrength ?? 0;

    const invalidPassword =
      this.userToEdit?.password &&
      (passwordStrength < 1 || this.userToEdit.password !== this.userToEdit.verifyPassword);

    return !!this.loadingSave || !!phoneInvalid || !!noPassword || !!invalidPassword;
  }

  didLoadAccounts: boolean;
  modal: HTMLIonModalElement;

  constructor(
    public readonly userService: UserService,
    private readonly errorService: ErrorService,
    private readonly notificationService: NotificationService,
    private readonly accountService: AccountService,
    private readonly channelService: ChannelService,
    private readonly alertController: AlertController,
    private readonly translate: TranslateService
  ) {
    super(userService);
  }

  ngOnInit(): void {
    if (this.stepObservable) {
      this.stepObservable.asObservable().subscribe((step) => {
        this.currentStep = step;
      });
    }

    this.loadUser();
    this.loadChannels();

    if (this.currentStep === 6 && !this.didLoadAccounts) {
      this.loadAccounts();
    }
  }

  private async loadUser(): Promise<void> {
    this.loadingUser = true;
    await this.errorService.resolve(
      this.userService.getTeamMemberById(this.userService.currentUser!.id),
      {
        errorMessage: 'components.onboardingCarousel.errors.loadUser',
        successCallback: (user) => {
          this.userToEdit = user;
          this.setDefaults(this.userToEdit);
        },
      }
    );

    this.loadingUser = false;
  }

  private setDefaults(user: User): void {
    user.notificationPreferences.email.alert = '1';
    user.notificationPreferences.email.approval = '1';
    user.notificationPreferences.email.info = '1';
  }

  private async loadAccounts(): Promise<void> {
    const paginatedAccounts = await this.errorService.resolve(this.accountService.getAccounts(), {
      errorMessage: 'components.accountConnect.errors.getAccounts',
    });

    this.accounts = paginatedAccounts ? paginatedAccounts.items : undefined;
    this.didLoadAccounts = true;
  }

  private async loadChannels(): Promise<void> {
    await this.errorService.resolve(this.channelService.getChannels(), {
      successCallback: (channelResponse) => {
        this.numChannels = channelResponse.items.length;
      },
    });
  }

  private serializeUserForAPI(user: User): SetTeamMemberParams {
    const body: SetTeamMemberParams = { id: user.id };

    if (user.firstName && user.firstName.trim()) {
      body.firstName = user.firstName;
    }

    if (user.lastName && user.lastName.trim()) {
      body.lastName = user.lastName;
    }

    if (user.email && user.email.trim()) {
      body.email = user.email;
    }

    if (user.country && user.country.trim()) {
      body.country = user.country;
    }

    if (user.password && user.password.trim()) {
      body.password = user.password;
    }

    if (user.parsedPhone && user.parsedPhone.nationalNumber.length === 10) {
      // @ts-ignore
      body.phone = user.parsedPhone.number;
    }

    body.notificationPreferences = user.notificationPreferences;
    return body;
  }

  passwordStrengthChange(passwordStrength: number | null): void {
    this.passwordStrength = passwordStrength;
  }

  async mutStep(value: number): Promise<void> {
    let didError = false;
    const movingForward = value > this.currentStep;

    if (movingForward) {
      this.loadingSave = true;

      switch (value) {
        case 3: {
          const body = this.serializeUserForAPI(this.userToEdit!);
          const updatedUser = await this.errorService.resolve(
            this.userService.setTeamMember(body),
            {
              errorMessage: 'components.onboardingCarousel.errors.saveUser',
            }
          );

          if (updatedUser) {
            this.userToEdit = updatedUser;
            this.setDefaults(updatedUser);
          } else {
            didError = true;
          }

          break;
        }
        case 4: {
          const response = await this.errorService.resolve(
            this.notificationService.setNotificationPreferences({
              teamMemberId: this.userToEdit!.id,
              preferences: {
                sms: this.userToEdit!.notificationPreferences.sms,
                email: this.userToEdit!.notificationPreferences.email,
                nsl: this.userToEdit!.notificationPreferences.nsl,
              },
            }),
            {
              errorMessage: 'components.onboardingCarousel.errors.saveNotifications',
            }
          );

          if (response && this.userToEdit) {
            this.userToEdit.notificationPreferences = response;
          } else {
            didError = true;
          }
          break;
        }
        case 5: {
          const doLaterText = await firstValueFrom(
            this.translate.get('components.onboardingCarousel.buttons.doLater')
          );
          const gotItText = await firstValueFrom(this.translate.get('gotItText'));

          if (this.accounts?.length === 0) {
            const header = await firstValueFrom(
              this.translate.get('components.onboardingCarousel.titles.accountAlertHeader')
            );

            const message = await firstValueFrom(
              this.translate.get('components.onboardingCarousel.labels.accountAlertMessage')
            );

            const alert = await this.alertController.create({
              header,
              message,
              buttons: [
                {
                  text: doLaterText,
                  handler: () => {
                    // if skipping account add then skip channel related steps.
                    this.mutStep(value + 3);
                  },
                },
                {
                  text: gotItText,
                  role: 'cancel',
                },
              ],
            });

            await alert.present();
            didError = true;
          } else {
            const errors = <string[]>(
              await Promise.all(
                ['facebook', 'linkedin'].map((social) => this.checkPagesOrGroups(social))
              )
            ).filter((error) => {
              return error !== undefined;
            });

            if (errors.length) {
              const header = await firstValueFrom(
                this.translate.get('components.onboardingCarousel.titles.connectionIssues')
              );

              const alert = await this.alertController.create({
                header,
                message: errors.join('\n\n'),
                cssClass: 'connection-issues-alert',
                buttons: [
                  {
                    text: doLaterText,
                    handler: () => {
                      this.currentStep = value;
                      this.stepChange(this.currentStep);
                    },
                  },
                  {
                    text: gotItText,
                    role: 'cancel',
                  },
                ],
              });
              await alert.present();
              didError = true;
            }

            // didError = await this.checkPagesOrGroups('facebook');

            // if (!didError) {
            //   didError = await this.checkPagesOrGroups(
            //     'linkedIn',
            //     didError,
            //     doLaterText,
            //     gotItText,
            //     value
            //   );
            // }
          }
          break;
        }
        case 7: {
          // if (addChannel) {
          // const newChannel = await this.editChannel?.doSave();
          // if (newChannel) {
          //   this.onboardingChannel = await this.errorService.resolve(this.channelService.getChannelById({
          //     id: newChannel.id,
          //     includeChannelSubscriptionSettings: 1,
          //     includeAccountSummary: 1,
          //     includeContentSources: 1,
          //     includePostPreviews: 3
          //   }));
          // }
          // }

          if (this.numChannels === 0) {
            didError = true;
            this.finishOnboarding();
          }
          break;
        }
        case 8: {
          didError = true;
          this.finishOnboarding();
          break;
        }
      }

      this.loadingSave = false;
    }

    if (value === 6) {
      if (
        this.numChannels > 0 ||
        !this.userService.currentUser!.userCanCreateContent ||
        this.userService.currentUser!.isAdmin
      ) {
        didError = true;
        if (movingForward) {
          this.mutStep(value + 1);
        } else {
          this.mutStep(value - 1);
        }
      }
    }

    if (!didError) {
      this.currentStep = value;
      this.stepChange(this.currentStep);
    }
  }

  accountsLoaded(accounts: Account[]): void {
    this.accounts = accounts;
    this.didLoadAccounts = true;
  }

  async checkChannel(value: number): Promise<void> {
    const addChannelErrors = await this.editChannel?.saveCheck();
    if (addChannelErrors !== 'errors') {
      this.mutStep(value);
    }
  }

  private async finishOnboarding(): Promise<void> {
    await this.errorService.resolve(
      this.userService.onboardTeamMember(this.userService.currentUser!.id),
      {
        errorMessage: 'components.onboardingCarousel.errors.onboard',
      }
    );

    const onboardedUser = await this.userService.getTeamMemberById(
      this.userService.currentUser!.id,
      {
        includePermissionInfo: 1,
        includeClientInfo: 1,
      }
    );

    if (onboardedUser.isOnboardedNum) {
      this.userService.currentUser = onboardedUser;
    }

    this.modal.dismiss();
  }

  private stepChange(step: number): void {
    if ('emit' in this.ionChangeStep) {
      this.ionChangeStep.emit(step);
    } else {
      this.ionChangeStep(step);
    }
  }

  private async checkPagesOrGroups(social: string): Promise<string | undefined> {
    let socialAdded = false;
    let profileOrGroup = false;

    this.accounts?.forEach((account) => {
      socialAdded = account.service === social ? true : socialAdded;
      profileOrGroup =
        (account.type === 'page' || account.type === 'group') && account.service === social
          ? true
          : profileOrGroup;
    });

    if (socialAdded && !profileOrGroup && this.isAdmin) {
      return firstValueFrom(
        this.translate.get(`components.onboardingCarousel.labels.${social}AccountAlertMessage`)
      );
    }

    return undefined;
  }
}
